import type { DashboardUserProfile } from '@services/firebase'
import type {
  LearningDisplayTextValue,
  LearningLesson,
  LearningLocalizedTextMap,
  LearningWorkbookChapter,
  LearningWorkbookQuizItem,
} from '@services/firebase/learning'

export type LegacySelfStudyTextMap = LearningLocalizedTextMap

export interface LegacySelfStudyChapter {
  readonly id: string
  readonly idSection: string
  readonly title: LegacySelfStudyTextMap
  readonly description: LegacySelfStudyTextMap
  readonly icon?: string
  readonly featuredImage?: string
  readonly image?: string
  readonly percent: number | null
  readonly progressStatus: 'pending' | 'resolved'
  readonly sort?: number
  readonly source: LearningWorkbookChapter
}

export interface LegacySelfStudyLesson {
  readonly id: string
  readonly sort?: number
  readonly title: LegacySelfStudyTextMap
  readonly chapters: readonly LegacySelfStudyChapter[]
  readonly listening_workbooks: readonly LearningWorkbookQuizItem[]
  readonly source: LearningLesson
}

interface LegacyChapterExtraFields {
  readonly description?: LearningDisplayTextValue
}

interface SelfLearningEntry {
  readonly chapterId?: string
  readonly selftStudyId?: string
}

const emptyTextMap: LegacySelfStudyTextMap = {}
const SELF_STUDY_PARTIAL_PROGRESS_STEP_PERCENT = 20
const SELF_STUDY_PARTIAL_PROGRESS_MAX_PERCENT = 90

const chapterContentCountFields = [
  'contentCount',
  'contentsCount',
  'itemCount',
  'itemsCount',
  'publishedContentCount',
  'publishedContentsCount',
  'selfStudyContentCount',
  'totalContentCount',
  'totalContents',
  'totalWorkbooks',
  'vietnameseWorkbookCount',
  'vietnameseWorkbooksCount',
  'workbookCount',
  'workbooksCount',
] as const

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object')
}

function stripHtml(value: string) {
  return value
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

function containsHtml(value: string) {
  return /<\/?[a-z][\s\S]*>/i.test(value)
}

function toCleanText(value: unknown) {
  if (typeof value !== 'string') {
    return undefined
  }

  const cleanValue = stripHtml(value)

  return cleanValue.length > 0 ? cleanValue : undefined
}

function toTextMap(
  value: LearningDisplayTextValue | undefined,
  fallback?: string,
): LegacySelfStudyTextMap {
  if (typeof value === 'string') {
    return toCleanText(value) ? { en: toCleanText(value) } : fallback ? { en: fallback } : emptyTextMap
  }

  if (!isRecord(value)) {
    return fallback ? { en: fallback } : emptyTextMap
  }

  return Object.entries(value).reduce<LegacySelfStudyTextMap>(
    (textMap, [languageCode, text]) => {
      const cleanText = toCleanText(text)

      if (!cleanText) {
        return textMap
      }

      return {
        ...textMap,
        [languageCode]: cleanText,
      }
    },
    fallback ? { en: fallback } : emptyTextMap,
  )
}

function getTitleText(chapter: LearningWorkbookChapter) {
  const title = toTextMap(chapter.title, 'Lesson part')

  return Object.keys(title).length > 0 ? title : { en: 'Lesson part' }
}

function getDescriptionText(chapter: LearningWorkbookChapter) {
  const extraFields = chapter as LearningWorkbookChapter & LegacyChapterExtraFields
  const description = extraFields.description

  if (typeof description === 'string' && containsHtml(description)) {
    return getTitleText(chapter)
  }

  if (isRecord(description)) {
    const hasHtmlDescription = Object.values(description).some(
      (text) => typeof text === 'string' && containsHtml(text),
    )

    if (hasHtmlDescription) {
      return getTitleText(chapter)
    }
  }

  const descriptionText = toTextMap(description)

  return Object.keys(descriptionText).length > 0
    ? descriptionText
    : getTitleText(chapter)
}

function getSelfLearningEntries(profile: DashboardUserProfile | null) {
  const personalization = profile?.personalization

  if (!isRecord(personalization) || !Array.isArray(personalization.selfLearning)) {
    return []
  }

  return personalization.selfLearning.filter(
    (entry): entry is SelfLearningEntry => isRecord(entry),
  )
}

function toContentCount(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, Math.round(value))
  }

  if (typeof value === 'string') {
    const parsedValue = Number(value)

    if (Number.isFinite(parsedValue)) {
      return Math.max(0, Math.round(parsedValue))
    }
  }

  return null
}

function getChapterContentCount(chapter: LearningWorkbookChapter) {
  const chapterRecord = chapter as LearningWorkbookChapter & Record<string, unknown>

  for (const field of chapterContentCountFields) {
    const count = toContentCount(chapterRecord[field])

    if (count !== null) {
      return count
    }
  }

  return null
}

function getLegacyChapterPercent({
  chapterId,
  chapter,
  selfLearningEntries,
}: {
  readonly chapterId: string
  readonly chapter: LearningWorkbookChapter
  readonly selfLearningEntries: readonly SelfLearningEntry[]
}) {
  const chapterEntries = selfLearningEntries.filter(
    (entry) => entry.chapterId === chapterId,
  )

  if (chapterEntries.some((entry) => entry.selftStudyId === 'implementation')) {
    return 100
  }

  if (chapterEntries.length === 0) {
    return 0
  }

  const contentCount = getChapterContentCount(chapter)

  if (contentCount === null) {
    return Math.min(
      SELF_STUDY_PARTIAL_PROGRESS_MAX_PERCENT,
      chapterEntries.length * SELF_STUDY_PARTIAL_PROGRESS_STEP_PERCENT,
    )
  }

  const denominator = contentCount + 2
  const percent = Math.round((chapterEntries.length / denominator) * 100 * 100) / 100

  return Math.min(100, Math.max(0, percent))
}

export function mapLegacySelfStudyLessons({
  lessons,
  profile,
  progressStatus = 'resolved',
}: {
  readonly lessons: readonly LearningLesson[]
  readonly profile: DashboardUserProfile | null
  readonly progressStatus?: 'pending' | 'resolved'
}): readonly LegacySelfStudyLesson[] {
  const selfLearningEntries = getSelfLearningEntries(profile)

  return lessons.map((lesson) => ({
    id: lesson.id,
    sort: lesson.sort,
    title: toTextMap(lesson.title, 'Untitled lesson'),
    chapters: (lesson.chapters ?? []).map((chapter) => ({
      id: chapter.id,
      idSection: chapter.id,
      title: getTitleText(chapter),
      description: getDescriptionText(chapter),
      icon: chapter.icon,
      featuredImage: chapter.featuredImage,
      image: chapter.image,
      percent: progressStatus === 'resolved'
        ? getLegacyChapterPercent({
            chapterId: chapter.id,
            chapter,
            selfLearningEntries,
          })
        : null,
      progressStatus,
      sort: chapter.sort,
      source: chapter,
    })),
    listening_workbooks: lesson.listening_workbooks ?? [],
    source: lesson,
  }))
}
